<script setup lang="ts">
import TextWithTooltips from '@jaaluu/components/TextWithTooltips.vue';
import stores from '@jaaluu/stores';
import { t } from '@shared/i18n';
import { ChatService, OpenAiRoleType } from '@shared/openapi/jaaluu-api';
import { Language, LanguageLevel } from '@shared/openapi/jaaluu-api/types.gen';
import { computed, Ref, ref, watch } from 'vue';

const { langForeign, langNative, langOptions, langLevel } = stores.language.state;
const { handleDefaultError } = stores.error.funcs;
const { logoBallTrans } = stores.assets.state;

const chat: Ref<{ content: string; role: string; response_lang: string | null }[]> = ref([]);
const userMessage = ref('');
const isSending = ref(false);
const chatId: Ref<string | null> = ref(null);
const initialized = ref(false);
const teacherLang: Ref<Language | null> = ref(null);

watch(
  langForeign,
  (val) => {
    if (val && !teacherLang.value) teacherLang.value = val;
  },
  { immediate: true }
);

const teacherFlag = computed(
  () => langOptions.value.find((o) => o.value === teacherLang.value)?.flag
);

const switchTeacherLang = async () => {
  if (
    !teacherLang.value ||
    !langForeign.value ||
    !langNative.value ||
    !chatId.value ||
    !teacherLang.value
  )
    return;
  teacherLang.value =
    teacherLang.value === langForeign.value ? langNative.value : langForeign.value;
  const messages = [
    {
      role: OpenAiRoleType.SYSTEM,
      content: `User changed language to ${t('langs.' + teacherLang.value)}. Answer in ${t('langs.' + teacherLang.value)} language from now on.`
    }
  ];
  const resAdd: any = await ChatService.addChatMessageV1ChatAddChatMessagePost({
    query: { lang: teacherLang.value, chat_id: chatId.value },
    body: messages
  }).catch(handleDefaultError);
};

const scrollToBottom = () => {
  setTimeout(() => {
    const el: HTMLElement | null = document.getElementById('scrollAnchorTeacherChat');
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
    }
  }, 500);
};

const initChat = async () => {
  if (initialized.value || !langForeign.value || !langNative.value || !teacherLang.value) return;
  if (langLevel.value === LanguageLevel.A1_A2) {
    teacherLang.value = langNative.value;
  } else {
    teacherLang.value = langForeign.value;
  }

  const systemMsg = `You are a friendly ${t('langs.' + langForeign.value)} language teacher. Help the user learn and practice ${t('langs.' + langForeign.value)}. The user's native language is ${t('langs.' + langNative.value)}. `;

  const messages = [
    { role: OpenAiRoleType.SYSTEM, content: systemMsg },
    {
      role: OpenAiRoleType.SYSTEM,
      content: `Answer in ${t('langs.' + teacherLang.value)} language if the user did not change the language.`
    }
  ];
  const resAdd: any = await ChatService.addChatMessageV1ChatAddChatMessagePost({
    query: { lang: teacherLang.value, chat_id: null },
    body: messages
  }).catch(handleDefaultError);
  if (!resAdd?.data?.chat_id) return;
  chatId.value = resAdd.data.chat_id;

  isSending.value = true;
  const text = "Start by greeting the user and asking what they'd like to practice or learn today.";
  const res: any = await ChatService.answerToTextV1ChatAnswerToTextPost({
    query: { chat_id: chatId.value },
    body: { lang: teacherLang.value, text }
  }).catch(handleDefaultError);
  if (res?.data?.chat) {
    chat.value = res.data.chat.filter((m: any) => m.role !== OpenAiRoleType.SYSTEM);
    chatId.value = res.data.chat_id;
    scrollToBottom();
  }
  isSending.value = false;
  initialized.value = true;
};

const sendMessage = async () => {
  if (!langForeign.value || isSending.value || !userMessage.value.trim() || !teacherLang.value)
    return;
  isSending.value = true;
  const text = userMessage.value.trim();
  userMessage.value = '';

  const res: any = await ChatService.answerToTextV1ChatAnswerToTextPost({
    query: { chat_id: chatId.value },
    body: { lang: teacherLang.value, text }
  }).catch(handleDefaultError);

  if (res?.data?.chat) {
    chat.value = res.data.chat.filter((m: any) => m.role !== OpenAiRoleType.SYSTEM);
    chatId.value = res.data.chat_id;
    scrollToBottom();
  }
  isSending.value = false;
};
</script>

<template>
  <div style="position: relative" @vue:mounted="initChat">
    <!-- Loading state when waiting for first message -->
    <div v-if="!initialized" class="d-flex justify-center align-center" style="min-height: 200px">
      <div class="text-center">
        <v-progress-circular indeterminate color="primary" size="46" class="mb-4" />
        <div class="text-body-1">{{ t('chat.waiting_for_teacher') }}</div>
      </div>
    </div>

    <div class="pt-6">
      <div
        v-for="(message, idx) in chat"
        :key="idx"
        class="d-flex mt-2"
        :class="{ 'justify-end': message.role === OpenAiRoleType.ASSISTANT }"
      >
        <v-card
          v-if="idx > 0"
          class="pa-2 msg"
          elevation="2"
          :class="{
            'msg-assistant': message.role === OpenAiRoleType.ASSISTANT,
            'msg-user': message.role === OpenAiRoleType.USER
          }"
          :color="message.role === OpenAiRoleType.ASSISTANT ? undefined : 'primary'"
        >
          <v-avatar size="x-small" v-if="message.role === OpenAiRoleType.ASSISTANT">
            <v-img alt="Jaaluu" :src="logoBallTrans" />
          </v-avatar>
          <v-avatar size="x-small" color="primary-darken-1" v-else>
            <v-icon size="x-small" icon="mdi-account-circle" />
          </v-avatar>
          <div class="ml-2">
            <TextWithTooltips
              v-if="
                message.role === OpenAiRoleType.ASSISTANT &&
                message.response_lang &&
                message.response_lang == langForeign
              "
              :text="message.content"
            />
            <div v-else>{{ message.content }}</div>
          </div>
        </v-card>
      </div>
    </div>

    <div id="scrollAnchorTeacherChat" />
    <v-skeleton-loader v-if="initialized && isSending" type="sentences" height="40" class="mt-2" />

    <div class="d-flex align-end mt-3 mt-12">
      <v-textarea
        v-model="userMessage"
        rows="1"
        variant="underlined"
        clearable
        color="green"
        density="compact"
        hide-details
        :auto-grow="true"
        clear-icon="mdi-close"
        :placeholder="t('chat.your_message')"
        class="search"
        @keydown.enter.prevent="sendMessage"
      />
      <v-btn
        v-if="userMessage"
        size="x-small"
        class="ml-4"
        icon="mdi-send"
        :loading="isSending"
        color="green"
        variant="text"
        @click="sendMessage"
      />
    </div>
    <div class="d-flex justify-end mt-2 mb-6 abs-lang-button">
      <v-btn size="x-small" variant="text" @click="switchTeacherLang">
        <template #append>
          <v-img v-if="teacherFlag" :src="teacherFlag" width="26" height="18" cover />
        </template>
        {{ t('chat.switch_language') }}
      </v-btn>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.msg {
  max-width: 85%;
  display: flex;
  align-items: flex-start;
}
.msg-assistant {
  justify-content: flex-end;
}
.msg-user {
  float: left;
}
.search {
  width: 100%;
}
</style>
